import type {
  ChangeLogFilters,
  ChangeLogOperation,
} from "../../../clients/graphqlTypes.js";

/**
 * `first` counts actions (change groups), not entries — the server caps a page
 * at N whole groups within its own entry budget, so one request carries a full
 * viewport of action rows. The value is unchanged and needs no frontend logic.
 */
export const CHANGE_LOG_PAGE_SIZE = 50;

export type ChangeLogActor = {
  /**
   * The raw prefixed acting identity (`user:<uuid>` / `system:<source>` /
   * `integration:<name>`) — the exact value the actor filter accepts, so a facet
   * actor round-trips to the filter with no client-side reconstruction.
   */
  actorId: string;
  userId?: string | null;
  label?: string | null;
};

export type ChangeLogEntity = {
  entityId: string;
  entityType: string;
  /**
   * The entity's read-time-resolved display name (live → composite → live root →
   * captured field value → `null`, never the raw id — target-naming design D3).
   * Nullable/optional so this shared view-model stays structurally assignable from the
   * generated fragment, whose nullable GraphQL field is typed optional. Never a stored
   * entity label — that concept is gone (design D2).
   */
  name?: string | null;
  position?: string | null;
  isDeleted: boolean;
  /**
   * The `WORKSESSION`/`PLANSESSION` span sources (target-naming design D5): the SAME
   * live-first/captured sources the server composes `name` from, exposed raw so the
   * shared display rule (`entityDisplayLabel`) can compose the span in the VIEWER's
   * timezone instead of echoing the server's ISO render — resolved on every surface that
   * carries the entity, including a filter facet option (no change entries at all) and a
   * row whose own action never touched a span field. `null`/absent for every other kind.
   * Nullable/optional per the shared view-model convention (a generated fragment's
   * nullable GraphQL field types optional).
   */
  nameSpan?: {
    from?: string | null;
    till?: string | null;
    date?: string | null;
    durationMinutes?: number | null;
  } | null;
};

export type ChangeLogFieldValue = {
  recorded: boolean;
  value?: unknown;
  /**
   * The read-model-resolved display name for a reference field value (phase / lead /
   * role / assignee / building name), rendered in place of the raw id. Nullable/optional
   * so this shared view-model stays structurally assignable from the generated fragment,
   * whose nullable GraphQL field is typed optional; a registry miss or unresolvable id
   * leaves it null and the raw `value` renders via the tri-state renderer instead.
   */
  label?: string | null;
} | null;

/**
 * The aggregate-agnostic shape the shared change-log view renders. Both the quote
 * (`ChangeLogView_Entry`) and project (`ProjectChangeLog_Entry`) generated fragments
 * are structurally assignable to this — the components depend on the shape, not on a
 * specific operation's generated type.
 */
export type ChangeLogEntry = {
  id: string;
  occurredAt: string;
  /**
   * The CRUD verb (the "Created / Changed / Deleted" badge). Null/absent marks a
   * pure event that mutated nothing. Optional to stay structurally assignable from the
   * nullable generated fragment field.
   */
  operation?: ChangeLogOperation | null;
  entityType: string;
  field?: string | null;
  correlationId: string;
  actor: ChangeLogActor;
  entity: ChangeLogEntity;
  oldValue?: ChangeLogFieldValue;
  newValue?: ChangeLogFieldValue;
};

export type ChangeLogFacetsData = {
  actors: ChangeLogActor[];
  entities: ChangeLogEntity[];
  /** The distinct CRUD operations present in this slice (surplus to the current UI). */
  operations: ChangeLogOperation[];
  /** True when the slice has any pure event (an entry with no `operation`). */
  hasPureEvents: boolean;
};

/**
 * Maps a change-log entity to its display label and optional deep-link target
 * (design D7). The label feeds the action list (Target column), filter picker, and
 * detail modal; the link wraps the Target / modal entity cell when a target exists.
 * Aggregate-specific: a quote's `DOCUMENT` reads "Quote" and links its items; a
 * project's documents link to the document while the project row / unmapped / deleted
 * entities are label-only. The label is derived from the entity's name; `nameOverride`
 * lets the caller supply the name AS OF an action (design D2), preferred over the
 * read-time-resolved `name`; a stored label is never used. `aggregateLabel` names the
 * owning aggregate (e.g. "Quote") for a multi-entity action with no root entry to show.
 */
export type ChangeLogEntityStrategy = {
  entityLabel: (
    entity: ChangeLogEntity,
    nameOverride?: string | null
  ) => string;
  /** Router `to`, or `undefined` for label-only (project row, deleted, unmapped). */
  entityLink: (entity: ChangeLogEntity) => string | undefined;
  /** The owning aggregate's label (the multi-entity Target fallback, design D6). */
  aggregateLabel: () => string;
};

/** Multi-value filter state mapped 1:1 to the query's `ChangeLogFilters` (design D6/D9). */
export type ChangeLogFilterState = {
  entityIds: string[];
  /** The set of selected prefixed acting identities (`actorId` strings) (design D9). */
  actors: string[];
  /** Inclusive lower bound on `occurredAt`, ISO datetime string or null (design D5). */
  occurredFrom: string | null;
  /** Inclusive upper bound on `occurredAt`, ISO datetime string or null (design D5). */
  occurredTo: string | null;
};

export const EMPTY_CHANGE_LOG_FILTERS: ChangeLogFilterState = {
  entityIds: [],
  actors: [],
  occurredFrom: null,
  occurredTo: null,
};

/** Map the UI filter state to the query's `ChangeLogFilters` input (shared by consumers). */
export function toChangeLogQueryFilters(
  filters: ChangeLogFilterState
): ChangeLogFilters {
  return {
    entityIds: filters.entityIds,
    actors: filters.actors,
    occurredFrom: filters.occurredFrom,
    occurredTo: filters.occurredTo,
  };
}
